const normalizeText = (value) => (value || '').toString().trim().toLowerCase();
const roundMoney = (value) => Math.round((Number(value) || 0) * 100) / 100;

function normalizeVipStatus(value) {
    const normalized = normalizeText(value);
    if (normalized === 'vip' || normalized === 'non_vip' || normalized === 'any') {
        return normalized;
    }
    return 'non_vip';
}

function normalizeStopPhase(value) {
    const normalized = normalizeText(value);
    if (normalized === 'added_stop' || normalized === 'first_pickup' || normalized === 'any') {
        return normalized;
    }
    return 'first_pickup';
}

function normalizePickupLocationType(value) {
    return normalizeText(value) || 'any';
}

function wildcardOrMatch(ruleValue, targetValue) {
    const normalizedRuleValue = normalizeText(ruleValue);
    const normalizedTargetValue = normalizeText(targetValue);
    // Context value "any" is an explicit wildcard request from the caller.
    if (normalizedTargetValue === 'any') {
        return true;
    }
    return normalizedRuleValue === 'any' || normalizedRuleValue === normalizedTargetValue;
}

function ruleSpecificity(rule) {
    return [rule?.pickup_location_type, rule?.vip_status, rule?.stop_phase]
        .map(value => normalizeText(value))
        .reduce((count, value) => count + (value === 'any' ? 0 : 1), 0);
}

function matchesRule(rule, context) {
    if (rule?.is_active === false) {
        return false;
    }

    const normalizedContext = {
        pickupLocationType: normalizePickupLocationType(context?.pickupLocationType),
        vipStatus: normalizeVipStatus(context?.vipStatus),
        stopPhase: normalizeStopPhase(context?.stopPhase)
    };

    return wildcardOrMatch(rule?.pickup_location_type, normalizedContext.pickupLocationType)
        && wildcardOrMatch(rule?.vip_status, normalizedContext.vipStatus)
        && wildcardOrMatch(rule?.stop_phase, normalizedContext.stopPhase);
}

export function resolveWaitTimeGraceRule(rules = [], context = {}) {
    const matchingRule = [...rules]
        .filter(rule => matchesRule(rule, context))
        .sort((left, right) => {
            const specificityDiff = ruleSpecificity(right) - ruleSpecificity(left);
            if (specificityDiff !== 0) {
                return specificityDiff;
            }

            // Tie-breaker matters for app-provided arrays that may not enforce the DB unique index.
            const leftTimestamp = Date.parse(left.updated_at || left.created_at || '') || 0;
            const rightTimestamp = Date.parse(right.updated_at || right.created_at || '') || 0;
            return rightTimestamp - leftTimestamp;
        })[0];

    return matchingRule || null;
}

export function resolveWaitTimeCharge({
    rules = [],
    context = {},
    waitMinutes = 0,
    ratePerMinute = 0
} = {}) {
    const rule = resolveWaitTimeGraceRule(rules, context);
    const rawWaitMinutes = Math.max(0, Number(waitMinutes) || 0);
    const normalizedRate = Math.max(0, Number(ratePerMinute) || 0);
    const graceMinutes = Math.max(0, Number(rule?.grace_minutes) || 0);
    const billableMinutes = Math.max(0, rawWaitMinutes - graceMinutes);
    const waitCharge = roundMoney(billableMinutes * normalizedRate);

    return {
        rule,
        clockStartPoint: rule?.clock_start_point || null,
        graceMinutes,
        waitMinutes: rawWaitMinutes,
        billableMinutes,
        ratePerMinute: normalizedRate,
        waitCharge
    };
}
